/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatDistanceToNow, format } from 'date-fns';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import NotificationsStatusEnum from '../../pages/Home/enums/notifications-status.enum';
import apiService from '../../services/api.service';
import NotificationDetailModal from './NotificationDetailModal';

function NotificationItem({ notification, onStatusChange }) {
  const [user, setUser] = useState({ firstName: '', lastName: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const foundUser = await apiService.head.getUser(notification.userId);
        setUser(foundUser);
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    };
    fetchUser();
  }, [notification.userId]);

  const formatDate = (date) => {
    const now = new Date();
    const createdAt = new Date(date);
    const diff = now - createdAt;

    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return formatDistanceToNow(createdAt, { addSuffix: true });
    }
    return format(createdAt, 'dd MMM yyyy');
  };

  const handleOpenModal = async () => {
    if (notification.status === NotificationsStatusEnum.PENDING) {
      await apiService.head.updateNotification(notification._id, NotificationsStatusEnum.OPENED);
      onStatusChange(notification._id, NotificationsStatusEnum.OPENED);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = async (status) => {
    if (status) {
      await apiService.head.updateNotification(notification._id, status);
      onStatusChange(notification._id, status);
    }
    setIsModalOpen(false);
  };

  const isPending = notification.status === NotificationsStatusEnum.PENDING;

  return (
    <>
      <button
        type="button"
        className={cn(
          'w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors',
          isPending && 'bg-primary/5',
        )}
        onClick={handleOpenModal}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div
            className={cn(
              'shrink-0 rounded-full p-2 mt-0.5',
              isPending ? 'bg-destructive/10' : 'bg-muted',
            )}
          >
            <AlertTriangle
              className={cn('h-4 w-4', isPending ? 'text-destructive' : 'text-muted-foreground')}
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={cn('text-sm font-medium truncate', isPending && 'text-foreground')}>
                {notification.details.detailName}
              </span>
              <Badge variant={isPending ? 'destructive' : 'secondary'} className="shrink-0 text-xs">
                {notification.type.toLowerCase()}
              </Badge>
              {isPending && (
                <span className="h-2 w-2 rounded-full bg-primary shrink-0" title="New" />
              )}
            </div>

            <p className="text-sm text-muted-foreground line-clamp-2">
              <span className="font-medium text-foreground">
                {user.firstName} {user.lastName}
              </span>{' '}
              reported an issue with this material
            </p>

            <p className="text-xs text-muted-foreground mt-1">
              {formatDate(notification.createdAt)}
            </p>
          </div>

          {/* Arrow */}
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
        </div>
      </button>

      {isModalOpen && (
        <NotificationDetailModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          notification={notification}
          user={user}
        />
      )}
    </>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    details: PropTypes.shape({
      materialIds: PropTypes.arrayOf(PropTypes.string).isRequired,
      materialTypes: PropTypes.arrayOf(PropTypes.string),
      links: PropTypes.arrayOf(PropTypes.string),
      parentId: PropTypes.string.isRequired,
      parentName: PropTypes.string.isRequired,
      detailName: PropTypes.string.isRequired,
      materialNames: PropTypes.arrayOf(PropTypes.string).isRequired,
      description: PropTypes.string.isRequired,
    }).isRequired,
    createdAt: PropTypes.string.isRequired,
  }).isRequired,
  onStatusChange: PropTypes.func.isRequired,
};

export default NotificationItem;
